/**
 * Team — 4 variants (grid_3col, founders_feature, polaroid, photo_row)
 */
import type { TeamContent } from '../schemas/team';
import {
  Avatar,
  Body,
  Eyebrow,
  H2,
  H3,
  SectionFrame,
  VisualPlaceholder,
} from './primitives';

interface TM {
  content: TeamContent;
}

export function TeamGrid3Col({ content }: TM) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
          {content.subheadline && <Body>{content.subheadline}</Body>}
        </div>
        <div className="ls-grid ls-grid-3">
          {content.members.map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
              <Avatar src={m.avatar} name={m.name} size={64} />
              <H3 style={{ fontSize: '1.0625rem' }}>{m.name}</H3>
              <div style={{ fontSize: '0.85rem', color: 'var(--ls-accent)', fontWeight: 500 }}>{m.role}</div>
              {m.bio && <Body style={{ fontSize: '0.9rem' }}>{m.bio}</Body>}
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function TeamFoundersFeature({ content }: TM) {
  const founders = content.members.slice(0, 2);
  const rest = content.members.slice(2);
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 64 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-grid ls-grid-2" style={{ gap: 48 }}>
          {founders.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
              <Avatar src={m.avatar} name={m.name} size={96} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <H3>{m.name}</H3>
                <div style={{ fontSize: '0.9rem', color: 'var(--ls-accent)', fontWeight: 500 }}>{m.role}</div>
                {m.bio && <Body style={{ fontSize: '0.95rem' }}>{m.bio}</Body>}
              </div>
            </div>
          ))}
        </div>
        {rest.length > 0 && (
          <div className="ls-grid ls-grid-4" style={{ gap: 24 }}>
            {rest.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                <Avatar src={m.avatar} name={m.name} size={48} />
                <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{m.name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ls-text-muted)' }}>{m.role}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionFrame>
  );
}

export function TeamPolaroid({ content }: TM) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-grid ls-grid-3">
          {content.members.map((m, i) => (
            <div
              key={i}
              style={{
                background: 'var(--ls-surface)',
                padding: 12,
                paddingBottom: 24,
                borderRadius: 4,
                boxShadow: 'var(--ls-shadow-lg)',
                transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 1.5}deg)`,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <VisualPlaceholder src={m.avatar} aspectRatio="1 / 1" label={m.name} />
              <div style={{ textAlign: 'center', fontFamily: 'var(--ls-font-heading)' }}>
                <div style={{ fontWeight: 600 }}>{m.name}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--ls-text-muted)' }}>{m.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function TeamPhotoRow({ content }: TM) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-grid ls-grid-4" style={{ gap: 16 }}>
          {content.members.map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <VisualPlaceholder src={m.avatar} aspectRatio="3 / 4" label={m.name} />
              <div style={{ fontWeight: 500, fontSize: '0.95rem' }}>{m.name}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--ls-text-muted)' }}>{m.role}</div>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export const teamVariants = {
  grid_3col: TeamGrid3Col,
  founders_feature: TeamFoundersFeature,
  polaroid: TeamPolaroid,
  photo_row: TeamPhotoRow,
};
